// src/components/course/creator/CourseDetail.jsx
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Edit3, Eye, Users, Clock, Star, BookOpen, Play, FileText,
  HelpCircle, Plus, Settings, TrendingUp, MessageSquare, DollarSign,
  Share2, Download, Save
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import LessonManagement from './LessonManagement';
import { MathRenderer } from '../../math/MathRenderer';

const CourseDetail = ({ bookId, courseId, user, onNavigate }) => {
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (courseId && bookId) {
      loadCourseData();
    }
  }, [courseId, bookId]);

  const loadCourseData = async () => {
    try {
      setLoading(true);
      
      const [courseData, lessonsData] = await Promise.all([
        courseService.getCourse(bookId, courseId),
        courseService.getLessons(bookId, courseId)
      ]);

      if (!courseData || courseData.creatorId !== user.uid) {
        alert('You do not have permission to view this course');
        onNavigate('creator');
        return;
      }

      setCourse(courseData);
      setLessons(lessonsData);
      
    } catch (error) {
      console.error('Error loading course:', error);
      alert('Failed to load course details');
      onNavigate('creator');
    } finally {
      setLoading(false);
    }
  };

  const handleLessonsUpdate = (updatedLessons) => {
    setLessons(updatedLessons);
    
    // Update course stats
    const totalDuration = updatedLessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);
    setCourse(prev => ({ 
      ...prev, 
      duration: totalDuration, 
      lessonCount: updatedLessons.length 
    }));
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatPrice = (price) => {
    return price > 0 ? `$${price.toFixed(2)}` : 'Free';
  };

  const getStatusColor = (status) => {
    if (course?.isPublished) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'lessons', label: 'Lessons', icon: Play },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-start space-x-4">
          <button
            onClick={() => onNavigate('creator')}
            className="text-gray-400 hover:text-gray-600 mt-1"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{course?.title}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}>
                {course?.isPublished ? 'Published' : 'Draft'}
              </span>
            </div>
            <p className="text-gray-600">{course?.category}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => onNavigate('course', { courseId })}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="w-4 h-4" />
            <span>Preview</span>
          </button>
          <button
            onClick={() => onNavigate('creator-edit', { courseId })}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Students</p>
              <p className="text-2xl font-bold text-gray-900">{course?.enrollmentCount || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Lessons</p>
              <p className="text-2xl font-bold text-gray-900">{lessons.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {course?.rating ? course.rating.toFixed(1) : '0.0'}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${((course?.enrollmentCount || 0) * (course?.price || 0)).toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <CourseOverview course={course} lessons={lessons} />
          )}

          {activeTab === 'lessons' && (
            <LessonManagement 
              lessons={lessons}
              bookId={bookId}
              courseId={courseId}
              viewMode="creator"
              onLessonSelect={setSelectedLesson}
              selectedLesson={selectedLesson}
              onLessonsUpdate={handleLessonsUpdate}
              onNavigate={onNavigate}
            />
          )}

          {activeTab === 'analytics' && (
            <CourseAnalytics courseId={courseId} course={course} />
          )}

          {activeTab === 'settings' && (
            <CourseSettings 
              bookId={bookId}
              courseId={courseId} 
              course={course}
              onUpdate={setCourse}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Course Overview Component
const CourseOverview = ({ course, lessons }) => {
  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'text': return FileText;
      case 'quiz': return HelpCircle;
      default: return BookOpen;
    }
  };

  const renderLearningObjectives = (objectives) => {
    if (!objectives) return null;
    
    if (Array.isArray(objectives)) {
      return (
        <div className="space-y-2">
          {objectives.map((objective, index) => (
            <div key={index} className="flex items-start space-x-2">
              <span className="text-blue-600 mt-1">•</span>
              <MathRenderer 
                content={objective}
                enableMarkdown={true}
                enableMath={true}
                className="flex-1 text-gray-600"
              />
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof objectives === 'string') {
      return (
        <MathRenderer 
          content={objectives}
          enableMarkdown={true}
          enableMath={true}
          className="text-gray-600"
        />
      );
    }
    
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Course Preview Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-8 text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold mb-4">{course?.title}</h2>
            <div className="text-blue-100 mb-6 leading-relaxed">
              <MathRenderer 
                content={course?.description || 'No description available'}
                enableMarkdown={true}
                enableMath={true}
                className="text-blue-100"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-blue-200">Duration</p>
                <p className="font-semibold">{formatDuration(course?.duration)}</p>
              </div>
              <div>
                <p className="text-blue-200">Lessons</p>
                <p className="font-semibold">{lessons.length}</p>
              </div>
              <div>
                <p className="text-blue-200">Level</p>
                <p className="font-semibold">{course?.level || 'Beginner'}</p>
              </div>
              <div>
                <p className="text-blue-200">Price</p>
                <p className="font-semibold">
                  {course?.price > 0 ? `$${course.price.toFixed(2)}` : 'Free'}
                </p>
              </div>
            </div>
          </div>
          
          {course?.thumbnail.url && (
            <div className="rounded-lg overflow-hidden">
              <img 
                src={course.thumbnail.url} 
                alt={course.title}
                className="w-full h-64 object-cover"
              />
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Description</h3>
        <div className="text-gray-600 leading-relaxed">
          <MathRenderer 
            content={course?.description || 'No description provided.'}
            enableMarkdown={true}
            enableMath={true}
            className="text-gray-600"
          />
        </div>
      </div>

      {/* Learning Objectives */}
      {course?.learningObjectives && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Objectives</h3>
          <div>
            {renderLearningObjectives(course.learningObjectives)}
          </div>
        </div>
      )}

      {/* Prerequisites */}
      {course?.prerequisites && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Prerequisites</h3>
          <MathRenderer 
            content={course.prerequisites}
            enableMarkdown={true}
            enableMath={true}
            className="text-gray-600"
          />
        </div>
      )}

      {/* Target Audience */}
      {course?.targetAudience && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Audience</h3>
          <MathRenderer 
            content={course.targetAudience}
            enableMarkdown={true}
            enableMath={true}
            className="text-gray-600"
          />
        </div>
      )}
    </div>
  );
};

// Course Analytics Component
const CourseAnalytics = ({ courseId, course }) => {
  const mockData = {
    totalViews: 1247,
    totalEnrollments: course?.enrollmentCount || 0,
    totalRevenue: (course?.enrollmentCount || 0) * (course?.price || 0),
    avgRating: course?.rating || 0,
    completionRate: 76
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Views</p>
              <p className="text-2xl font-bold text-gray-900">{mockData.totalViews}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Enrollments</p>
              <p className="text-2xl font-bold text-gray-900">{mockData.totalEnrollments}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${mockData.totalRevenue.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">{mockData.completionRate}%</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Chart Placeholder */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Over Time</h4>
        <div className="h-64 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
          <p>Chart visualization would be implemented here</p>
        </div>
      </div>
    </div>
  );
};

// Course Settings Component
const CourseSettings = ({ bookId, courseId, course, onUpdate }) => {
  const [settings, setSettings] = useState({
    isPublished: course?.isPublished || false,
    featured: course?.featured || false,
    allowComments: course?.allowComments || true,
    allowDownloads: course?.allowDownloads || false,
    certificateEnabled: course?.certificateEnabled || true
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      await courseService.updateCourse(bookId, courseId, settings);
      onUpdate({ ...course, ...settings });
      alert('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Course Settings</h3>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Published Status</h4>
              <p className="text-sm text-gray-500">Make your course visible to students</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.isPublished}
                onChange={(e) => setSettings({ ...settings, isPublished: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Featured Course</h4>
              <p className="text-sm text-gray-500">Display this course prominently</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.featured}
                onChange={(e) => setSettings({ ...settings, featured: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Certificate Enabled</h4>
              <p className="text-sm text-gray-500">Issue certificates on completion</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.certificateEnabled}
                onChange={(e) => setSettings({ ...settings, certificateEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;